/* eslint-disable class-methods-use-this */
import debounce from 'lodash/debounce';
import {scrollTo} from 'Utils/scrollUtils';

/**
 * @description When we click on the domElement,
 *  we go to the target with smooth scrool.
 */
export default class Anchor {
    /**
     * @description We pass a HTMLElelement that will trigger the anchor.
     * @param {HTMLElement} domElement - The element that will
     * be used to trigger the Anchor.
     */
    constructor(domElement) {
        if(!domElement) {
            throw new Error('element is undefined');
        }

        this.config = {
            anchorId: domElement.getAttribute('data-anchor-id'),
            domElement,
            domTarget: () => document.querySelector(`#${this.config.anchorId}`)
        };

        if(!this.config.anchorId || !this.config.domTarget()) {
            throw new Error('no target for the Anchor or Id doesn\'t exist');
        }

        if(this.config.domElement
            .getAttribute('data-anchor-initialized') === 'true') {
            throw new Error('Module already initialized');
        }

        this.config.domElement.dataset.anchorInitialized = 'true';

        Object.defineProperties(this.config, {
            targetPosition: {
                value: this.getElementPosToTop(this.config.domTarget())
            },
            timing: {
                value: Number(this.config.domElement
                    .getAttribute('data-anchor-timing'))
            },
            toAddToAnchorPoint: {
                value: document.querySelector(this.config.domElement
                    .getAttribute('data-anchor-addtoscroll'))
            }
        });

        this.initListeners();
    }

    /**
     * @description Calculate the number of pixel between the component
     * and the top of the page.
     * @param  {HTMLElement} target - Element that will be used
     * for the calculate.
     * @returns {number} The number of pixel between
     * the target and the top.
     */
    getElementPosToTop(target) {
        return target.getBoundingClientRect().top + window.pageYOffset;
    }

    /**
     * @description Calculate the  height of the target.
     * @param {HTMLElement} target - Will be used to calculat the height.
     * @param {number} extraHeight - Will add some extra height
     * to the component.
     * @returns {number} The height of the component.
     */
    getTargetHeight(target, extraHeight = 0) {
        return target && target.clientHeight + extraHeight;
    }

    /**
     * @description Calc the number of pixel of the scroll.
     * @param {number} posToTop - Position value from the element to the top.
     * @param {number} ToAddToScroll - Value to remove to the scroll position.
     * @returns {number} Calculate the number of pixel between
     * the trigger and the anchor.
     */
    calcToScrollVal(posToTop, ToAddToScroll) {
        return ToAddToScroll ? posToTop - ToAddToScroll : posToTop;
    }

    /**
     * @description Init the listeners of the anchor.
     */
    initListeners() {
        const DEBOUNCE = '200';
        const EXTRAHEIGHT = 10;

        this.config.domElement.addEventListener('click', debounce(() => {
            scrollTo(
                window.pageYOffset,
                this.calcToScrollVal(
                    this.config.targetPosition,
                    this.getTargetHeight(
                        this.config.toAddToAnchorPoint,
                        EXTRAHEIGHT
                    )
                ),
                this.config.timing
            );
        }, DEBOUNCE));

        window.addEventListener('resize', debounce(() => {
            this.config.targetPosition = this.getElementPosToTop(this.config
                .domTarget());
        }, DEBOUNCE));
    }
}
